import React, { useMemo, useState } from "react";
import moment from "moment";

import { Checkbox, Tooltip } from "antd";
import {
  DeleteOutlined, EditOutlined, EyeInvisibleOutlined, MinusCircleOutlined, PlusCircleOutlined,
} from "@ant-design/icons";
import { Table } from "react-bootstrap";

import StatusTag from "../../Common/components/StatusTag";
import {
  formatMlRoutingFlagsAndReasons,
  formatMlRoutingStatus,
} from "../Dashboard/MLRouting/mlRoutingUtils";

import { getUser } from "../../../utils/helpers";
import { BATCH_REPORT_TEST_STATUS, BATCH_REPORT_GENERATE_STATUS } from "../../../utils/statusConfig";

import "./BatchReportsTableNew.css";

const userCorrectionMap = {
  1: "Approved",
  2: "Rejected",
  Approved: 1,
  Rejected: 2,
};

/** ESV Test Spec Flag → display label (label-only; from ESV SPEC_FLAGS_MAP). */
const SPEC_FLAGS_MAP = {
  invalid: { label: "-" },
  "-3": { label: "Deleted by Lab" },
  "-5": { label: "Deleted by User" },
  "-4": { label: "Setup Required" },
  0: { label: "In Process" },
  1: { label: "In Spec" },
  2: { label: "Out of Spec" },
  3: { label: "Spec Incomplete" },
  4: { label: "In Process (Retest)" },
  5: { label: "In Spec (Retest)" },
  6: { label: "Out of Spec (Retest)" },
  7: { label: "Out of Trend" },
  8: { label: "Out of Spec" },
};

/**
 * Tooltip rows when present_on_esv is true (display order).
 * Cell value uses esv_result.
 */
const ESV_TOOLTIP_FIELDS = [
  { label: "Test Spec", key: "test_spec" },
  { label: "Test Spec Flag", key: "test_spec_flag" },
  { label: "QC Message", key: "esv_message" },
  { label: "Partial Report Generated", key: "partial_report_generated" },
  { label: "Report Sent", key: "report_sent" },
  { label: "In Review", key: "is_under_review" },
  { label: "Release Expiry", key: "review_results_expiry_date" },
];

const getSpecFlagLabel = (flag) => {
  if (flag == null || flag === "") return "-";
  const mapped = SPEC_FLAGS_MAP[flag] ?? SPEC_FLAGS_MAP[String(flag)];
  return mapped?.label ?? String(flag);
};

/** UTC timestamp → local display (same pattern as other tabs: MM/DD/YYYY, h:mm:ss a). */
const formatEsvUtcDate = (utcValue) => {
  if (utcValue == null || utcValue === "") return "-";
  const local = moment.utc(utcValue).local();
  return local.isValid() ? local.format("MM/DD/YYYY, h:mm:ss a") : "-";
};

const formatEsvTooltipValue = (key, value) => {
  if (key === "test_spec_flag") return getSpecFlagLabel(value);
  if (key === "review_results_expiry_date") return formatEsvUtcDate(value);
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value == null || value === "") return "-";
  return String(value);
};

/** Dark AntD tooltip — bold Label: value rows, or a single message string. */
const renderEsvStatusTooltip = (esvData) => {
  if (!esvData?.present_on_esv) {
    return "Not present in ESV";
  }
  return (
    <div className="BatchReportsTableNew__EsvTooltip">
      {ESV_TOOLTIP_FIELDS.map(({ label, key }) => (
        <div key={key} className="BatchReportsTableNew__EsvTooltipRow">
          <span className="BatchReportsTableNew__EsvTooltipLabel">
            {label}
            :
          </span>
          {" "}
          <span className="BatchReportsTableNew__EsvTooltipValue">
            {formatEsvTooltipValue(key, esvData[key])}
          </span>
        </div>
      ))}
    </div>
  );
};

const mlRecordFromTest = (element) => {
  const pairs = formatMlRoutingFlagsAndReasons(element?.ml_reasons);
  const rawStatus = element?.ml_status;
  const hasStatus = rawStatus != null && String(rawStatus).trim() !== "";
  if (!hasStatus && pairs.length === 0) return null;
  return {
    status: formatMlRoutingStatus(rawStatus),
    pairs,
  };
};

const makeReasonArray = (reason) => {
  let inputString = reason || "";
  while (inputString.charAt(0) === "\n") {
    inputString = inputString.slice(1);
  }
  while (inputString.charAt(inputString.length - 1) === "\n") {
    inputString = inputString.slice(0, -1);
  }
  return inputString.split("\n");
};

const BatchReportsTableNew = ({
  previewTableData,
  selectPartial,
  toggleIcon,
  handleClick,
  handleSelectTest,
  handleSelectSample,
  handleEditResult,
  resultTextArea,
}) => {
  const [expanded, setExpanded] = useState(new Set());
  const [expandedMl, setExpandedMl] = useState(new Set());
  const user = getUser();

  const expandQCRow = (rowIndex, expand = true) => {
    const expandedRows = new Set(expanded);
    if (expand) {
      // If row needs to be expanded
      expandedRows.add(rowIndex);
    } else {
      // is row needs to be hidden
      expandedRows.delete(rowIndex);
    }
    setExpanded(expandedRows);
  };

  const showEsvStatusColumn = previewTableData?.wo_esv_data === true;

  const expandMlRow = (rowIndex, expand = true) => {
    const expandedRows = new Set(expandedMl);
    if (expand) {
      expandedRows.add(rowIndex);
    } else {
      expandedRows.delete(rowIndex);
    }
    setExpandedMl(expandedRows);
  };

  const showMlColumn = useMemo(() => (
    (previewTableData?.info_list || []).some((element) => Boolean(mlRecordFromTest(element)))
  ), [previewTableData]);

  const columns = useMemo(() => {
    const cols = [
      { label: "GenistaID", visible: true, classname: "BatchReportPreview__GidTd" },
      { label: "Test", visible: true, classname: "" },
      { label: "Result", visible: user.permissionMap.view_test_result_batchreports, classname: "" },
      { label: "QC", visible: true, classname: "BatchReportTableNew__MergeQCCell" },
      { label: "ML Action", visible: showMlColumn, classname: "BatchReportTableNew__MlCell" },
      { label: "Status", visible: true, classname: "" },
      { label: "ESV Status", visible: showEsvStatusColumn, classname: "BatchReportsTableNew__EsvStatusCell" },
    ];
    return cols.filter((col) => col.visible);
  }, [user, showMlColumn, showEsvStatusColumn]);

  /**
   * Return the correct text color based on the test status
   * 1. Red: Retest or Pending
   * 2. Green: Ready or Ready-P
   * 3. Pink: Complete
   * @param {*} test_status
   * @returns
   */
  // const getTestStatusClassName = (test_status, partial) => {
  //   if (test_status === "Complete") {
  //     return partial ? "BatchReportPreview__TestStatus--light-pink" : "BatchReportPreview__TestStatus--pink";
  //   }

  //   if (test_status === "Ready") {
  //     return "BatchReportPreview__TestStatus--green";
  //   }

  //   if (test_status === "Ready-P") {
  //     return partial ? "BatchReportPreview__TestStatus--light-green" : "BatchReportPreview__TestStatus--green";
  //   }

  //   /** Retest, Pending, Hold-Micro */
  //   return partial ? "BatchReportPreview__TestStatus--light-red" : "BatchReportPreview__TestStatus--red";
  // };

  /**
   * Render the QC details row under each test row
   * @param {*} element test data
   */
  const renderQcDetails = (element, { greyoutTest, color }) => {
    const reasons = makeReasonArray(element.qc_status);
    return (
      <tr>
        <td colSpan={columns.length} style={{ backgroundColor: color }}>
          <div className={`BatchReportsTableNew__QcDetails ${selectPartial ? greyoutTest : ""}`}>
            <div className="BatchReportsTableNew__QcDetailsCell">
              <p className="BatchReportsTableNew__QcDetailsCell--TextDark QCLabel">Manual QC</p>
              <StatusTag result={element.manual_qc_result} type="MANUAL_QC" />
              <p className="BatchReportsTableNew__QcDetailsCell-TextLight">
                {element.manual_qc_status || "-"}
              </p>
            </div>
            <div className="BatchReportsTableNew__QcDetailsCell">
              <span className="BatchReportsTableNew__QcDetailsCell--TextDark QCLabel">Software QC:</span>
              <StatusTag result={element.qc_result} type="QC" />
              {reasons.length ? (
                <div>
                  {reasons.map((reason, index_r) => (
                    <p key={index_r} className="BatchReportsTableNew__QcDetailsCell-TextLight">
                      {reason || "-"}
                    </p>
                  ))}
                </div>
              ) : "-"}
            </div>
            <div className="BatchReportsTableNew__QcDetailsCell">
              <span className="BatchReportsTableNew__QcDetailsCell--TextDark QCLabel">AI QC:</span>
              <StatusTag result={element.ml_qc_result} type="QC">
                {element.ml_qc_correction && (
                  <span className="BatchReportsTableNew__QcDetailsCell__UserCorrectionText">
                    &nbsp;-&nbsp;
                    {userCorrectionMap[element.ml_qc_correction]}
                  </span>
                )}
              </StatusTag>
              <p className="BatchReportsTableNew__QcDetailsCell-TextLight">
                {element.ml_qc_status || "-"}
              </p>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderMlDetails = (mlRecord, { greyoutTest, color }) => {
    const pairs = mlRecord?.pairs || [];
    return (
      <tr>
        <td colSpan={columns.length} style={{ backgroundColor: color }}>
          <div className={`BatchReportsTableNew__MlDetails ${selectPartial ? greyoutTest : ""}`}>
            {pairs.length > 0 ? pairs.map((pair, pairIndex) => (
              <div key={pairIndex} className="BatchReportsTableNew__MlDetailsRow">
                <p className="BatchReportsTableNew__MlDetailsLabel">{pair.flag || "-"}</p>
                <p className="BatchReportsTableNew__MlDetailsText">{pair.reason || "-"}</p>
              </div>
            )) : (
              <div className="BatchReportsTableNew__MlDetailsRow">
                <p className="BatchReportsTableNew__MlDetailsText">-</p>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  /**
   * ESV Status cell (only when wo_esv_data is true):
   * - present_on_esv true  → esv_result + detail tooltip
   * - present_on_esv false → "-" + "Not present in ESV" tooltip
   */
  const renderEsvStatusCell = (element, greyoutTest) => {
    const esvData = element?.esv_data;
    const presentOnEsv = esvData?.present_on_esv === true;
    let cellValue = "-";
    if (presentOnEsv && esvData.esv_result != null && esvData.esv_result !== "") {
      cellValue = esvData.esv_result;
    }
    return (
      <td className={selectPartial ? greyoutTest : ""}>
        <Tooltip
          placement="leftTop"
          autoAdjustOverflow
          destroyTooltipOnHide
          getPopupContainer={() => document.body}
          title={renderEsvStatusTooltip(esvData)}
          overlayClassName="BatchReportsTableNew__EsvStatus__Tooltip"
        >
          <span className="BatchReportsTableNew__EsvStatusValue">
            {cellValue}
          </span>
        </Tooltip>
      </td>
    );
  };

  /**
   * Render each column for test
   * @param {*} index index of the test from info_list
   * @param {*} element test detail
   */
  const showTable = (index, element) => {
    const color = element.grey_color ? "#F4F5F7" : "#FFFFFF";
    const dueDate = element.due_by && previewTableData.showOverdueIconAndTooltip ? element.due_by : "";
    let genista_id = element.genista_id;
    // only show one gid for all its tests
    if (index > 0 && element.genista_id === previewTableData.info_list[index - 1].genista_id) {
      genista_id = "";
    }
    const greyoutSample = element.greyoutSample ? "BatchReportPreview__Greyout" : "";
    const greyoutTest = element.greyout ? "BatchReportPreview__Greyout" : "";
    const partialTooltipTitle = element.greyout && selectPartial ? "Partial Report can only be generated for Tests in Ready Status" : "";
    const allowSelectSample = genista_id !== "" && selectPartial && element.fixedAvailableSelect > 0;
    const allowSelectTest = !element.greyout && selectPartial;
    const gidPointerClass = allowSelectSample ? "BatchReportPreview__Pointer" : "";
    const testPointerClass = allowSelectTest ? "BatchReportPreview__Pointer" : "";
    const mlRecord = mlRecordFromTest(element);

    return (
      <React.Fragment key={index}>
        <Tooltip
          placement="left"
          title={partialTooltipTitle}
          overlayClassName="BatchReportPreview__Partial__Tooltip"
        >
          <tr
            key={index}
            style={{ backgroundColor: color }}
          >
            <td
              className={`BatchReportPreview__GidTd ${gidPointerClass}`}
              onMouseOver={() => toggleIcon(index, "enter", "showDeleteSample")}
              onFocus={() => null}
              onMouseLeave={() => toggleIcon(index, "leave", "showDeleteSample")}
              onClick={() => handleSelectSample(allowSelectSample ? !element.sample_check : null, index)}
            >
              <div className={selectPartial ? `BatchReportPreview__GidTd__Content ${greyoutSample}` : "BatchReportPreview__GidTd__Content BatchReportPreview__SpaceBetween"}>
                {selectPartial
                  && !element.greyoutSample
                  && genista_id !== ""
                  && (
                    <Checkbox
                      checked={element.sample_check}
                    />
                  )}
                {
                  element.hidden === 1 && user.permissionMap.view_hidden_sample_icon_automation && genista_id !== "" ? (
                    <span>
                      <EyeInvisibleOutlined />
                      &nbsp;
                      {genista_id}
                    </span>
                  ) : genista_id
                }
                {element.showDeleteSample
                  && genista_id !== ""
                  && !selectPartial
                  && (
                    <DeleteOutlined
                      onClick={() => handleClick("Sample", { wo_id: previewTableData.wo_id, genista_id: element.genista_id, target: genista_id })}
                      className="BatchReportPreview__DeleteIcon"
                    />
                  )}
              </div>
            </td>
            <td
              onMouseOver={() => toggleIcon(index, "enter", "showDeleteTest")}
              onFocus={() => null}
              onMouseLeave={() => toggleIcon(index, "leave", "showDeleteTest")}
              onClick={() => handleSelectTest(allowSelectTest ? !element.test_check : null, index, "row")}
            >
              <Tooltip
                placement="top"
                title={dueDate}
                overlayClassName="BatchReportPreview__Overdue__Tooltip"
                arrow={false}
                key={index}
              >
                <div className={selectPartial ? `BatchReportPreview__TestStatusTd__Content ${greyoutTest} ${testPointerClass}` : "BatchReportPreview__TestStatusTd__Content BatchReportPreview__SpaceBetween"}>
                  {selectPartial
                    && !element.greyout
                    && (
                      <Checkbox
                        checked={element.test_check}
                      />
                    )}
                  <div className="BatchReportPreview__PreviewTableWOFlex">
                    {element.test_overdue === 1 && previewTableData.showOverdueIconAndTooltip ? <i className="fa fa-clock-o batchWOOverDue" /> : null}
                    {element.internal_name}
                  </div>
                  {element.showDeleteTest && !selectPartial
                    && (
                      <DeleteOutlined
                        onClick={() => handleClick("Test", {
                          wo_id: previewTableData.wo_id,
                          genista_id: element.genista_id,
                          internal_name: element.internal_name,
                          target: element.internal_name,
                        })}
                        className="BatchReportPreview__DeleteIcon"
                      />
                    )}
                </div>
              </Tooltip>
            </td>
            {user.permissionMap.view_test_result_batchreports && (
              <td
                onMouseOver={() => (user.permissionMap.change_test_result_batchreports ? toggleIcon(index, "enter", "showEditResult") : null)}
                onFocus={() => null}
                onMouseLeave={() => (user.permissionMap.change_test_result_batchreports ? toggleIcon(index, "leave", "showEditResult") : null)}
              >
                {!element.edit_result && (
                  <div className={selectPartial ? `BatchReportPreview__EditResult ${greyoutTest}` : "BatchReportPreview__EditResult BatchReportPreview__SpaceBetween"}>
                    <div className="BatchReportPreview__EditResult__Text">{element.result}</div>
                    {!selectPartial
                      && element.showEditResult
                      && element.test_category !== "MRL"
                      && element.test_status !== "Complete"
                      && (
                        <EditOutlined
                          className="BatchReportPreviewTable__Edit"
                          onClick={() => handleEditResult(index)}
                        />
                      )}
                  </div>
                )}
                {element.edit_result && resultTextArea(element, index)}
              </td>
            )}
            <td className={selectPartial ? greyoutTest : ""}>
              <div className="BatchReportTableNew__MergeQCColumn">
                <span style={BATCH_REPORT_GENERATE_STATUS[element.generate_status]?.style || {}}>{BATCH_REPORT_GENERATE_STATUS[element.generate_status]?.label || "-"}</span>
                {expanded.has(index)
                  ? <MinusCircleOutlined onClick={() => expandQCRow(index, false)} />
                  : <PlusCircleOutlined onClick={() => expandQCRow(index, true)} />}
              </div>
            </td>
            {showMlColumn && (
              <td className={`BatchReportTableNew__MlCell ${selectPartial ? greyoutTest : ""}`}>
                <div className="BatchReportTableNew__MergeQCColumn">
                  <span>{mlRecord?.status || "-"}</span>
                  {expandedMl.has(index)
                    ? <MinusCircleOutlined onClick={() => expandMlRow(index, false)} />
                    : <PlusCircleOutlined onClick={() => expandMlRow(index, true)} />}
                </div>
              </td>
            )}
            <td className={`${selectPartial ? greyoutTest : ""}`} style={BATCH_REPORT_TEST_STATUS[element.test_status]?.style || {}}>{element.test_status}</td>
            {showEsvStatusColumn && renderEsvStatusCell(element, greyoutTest)}
          </tr>
        </Tooltip>
        {expanded.has(index) && renderQcDetails(element, { greyoutTest, color })}
        {showMlColumn && expandedMl.has(index) && renderMlDetails(mlRecord, { greyoutTest, color })}
      </React.Fragment>
    );
  };

  return (
    <Table striped bordered hover className="table batchReportPreviewTable">
      <thead>
        <tr className="batchReportsPreviewTableHeading">
          {columns.map((col, idx) => (col.visible ? <td key={`${col.label}-${idx}`} className={col.classname}>{col.label}</td> : null))}
        </tr>
      </thead>
      <tbody id="batchReportPreviewTbody">
        {previewTableData.info_list && previewTableData.info_list.map((element, index) => showTable(index, element))}
      </tbody>
    </Table>
  );
};

export default BatchReportsTableNew;
